import React, { useState } from 'react';

import { Autocomplete, TextField, Typography, useTheme } from '@mui/material';
import type { GridFilterInputValueProps } from '@mui/x-data-grid-premium';
import clsx from 'clsx';
import { keys } from 'ramda';
import { AlineCheckbox, FilterTags } from 'src/components';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import { filterAutocompleteStyle } from './autocomplete-filter-utils';

const useStyles = makeStyles()(() => ({
  autocomplete: filterAutocompleteStyle,
  item: {
    alignItems: 'center',
    display: 'flex',
  },
}));

const PRIORITY_RECORD: Record<Apollo.WorkflowPriority, string> = {
  [Apollo.WorkflowPriority.Urgent]: 'Urgent',
  [Apollo.WorkflowPriority.High]: 'High',
  [Apollo.WorkflowPriority.Medium]: 'Medium',
  [Apollo.WorkflowPriority.Low]: 'Low',
};
const PRIORITY_OPTIONS: Apollo.WorkflowPriority[] = keys(PRIORITY_RECORD);

export function WorkflowPriorityAutocompleteFilter({
  applyValue,
  item,
}: GridFilterInputValueProps): JSX.Element {
  const theme = useTheme();
  const { classes } = useStyles();
  const [truncateTags, setTruncateTags] = useState(true);
  const [value, setValue] = useState<Apollo.WorkflowPriority[]>(item.value ?? []);

  function handleValueChange(value: Apollo.WorkflowPriority[]): void {
    applyValue({
      ...item,
      value,
    });
    setValue(value);
  }

  return (
    <Autocomplete<Apollo.WorkflowPriority, true, false, false>
      disableCloseOnSelect
      fullWidth
      multiple
      openOnFocus
      blurOnSelect={false}
      className={classes.autocomplete}
      getOptionLabel={option => option}
      options={PRIORITY_OPTIONS}
      renderInput={params => {
        return <TextField placeholder="Search priorities..." {...params} variant="standard" />;
      }}
      renderOption={(props, option, state) => {
        const { className, ...otherProps } = props;
        return (
          <li {...otherProps} className={clsx(className, classes.item)}>
            <AlineCheckbox checked={state.selected} style={{ marginRight: theme.spacing(1) }} />
            <Typography>{PRIORITY_RECORD[option]}</Typography>
          </li>
        );
      }}
      renderValue={tags => {
        return (
          <FilterTags<Apollo.WorkflowPriority>
            getTextFromTag={tag => PRIORITY_RECORD[tag]}
            tags={tags}
            truncateTags={truncateTags}
            onDelete={tag => handleValueChange(tags.filter(filterTag => tag !== filterTag))}
          />
        );
      }}
      slotProps={{
        popper: {
          modifiers: [
            {
              enabled: false,
              name: 'flip',
            },
            {
              enabled: false,
              name: 'preventOverflow',
            },
          ],
        },
      }}
      value={value}
      onBlur={e => {
        e.stopPropagation();
        setTruncateTags(true);
      }}
      onChange={(_e, value) => handleValueChange(value)}
      onFocus={e => {
        e.stopPropagation();
        setTruncateTags(false);
      }}
    />
  );
}
